<template>
  <div>
    <div>
      <v-data-table
        :headers="headers"
        :items="tableData"
        class="app-events-table "
        :loading="LOADING"
        :items-per-page="itemsPerPage"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        @page-count="pageCount = $event"
        @update:items-per-page="itemsPerPage = $event"
        :page.sync="C_PAGE"
        header-props.sort-icon="mdi-chevron"
        :sort-by.sync="sortBySync"
        :sort-desc.sync="sortDescSync"
      >
        <template v-slot:no-data>
          No results match your current filters, please try again.
        </template>
        <template #loading>
          <v-progress-circular
            class="centered-circle"
            indeterminate
            color="primary"
          ></v-progress-circular>
        </template>
        <template v-slot:item.eventStartDate="{ item }">
          <span>{{
              item.eventStartDate | humanTableTimeFormat(item.timezone)
            }}</span>
        </template>
        <template v-slot:item.eventEndDate="{ item }">
          <span>{{
              item.eventEndDate | humanTableTimeFormat(item.timezone)
            }}</span>
        </template>
        <template v-slot:item.organization="{ item }">
          <span v-show="!item.organization || !item.organization.length">-</span>
          <span v-for="(o, idx) in item.organization" :key="o.id">
            {{ o | humanOrganizationFilter
            }}<template v-if="idx < item.organization.length - 1">,<br/></template>
          </span>
        </template>
        <template v-slot:item.createdByEmail="{ item }">
          <div class="text-truncate" style="max-width: 100px">
            {{ item.createdByEmail }}
          </div>
        </template>
        <template v-slot:item.name="{ item }">
          <router-link
            :to="isReportingPage ? '/event/reporting/' + item.id : '/event/edit/' + item.id"
            class="event-name"
          >
            {{ item.name }}
          </router-link>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="flex-wrap justify-center d-flex flex-lg-nowrap">
            <template v-if="isReportingPage">
              <app-button class="ma-1 flex-grow-1" :to="'/event/reporting/' + item.id">View Report</app-button>
              <app-button
                :on-click="() => openReportingDialog(item)"
                :disabled="downloading"
                class="ma-1 flex-grow-1"
              >
                Download
              </app-button>
            </template>
            <template v-else>
              <app-button
                v-if="displayViewButton(item) && displayQRCodeButton(item)"
                :to="`/webqr/${item.id}`"
                class="ma-1 flex-grow-1"
                target="_blank"
              >
                QR Code
              </app-button>
              <app-button
                v-else-if="displayQRCodeButton(item)"
                :to="`/webqr/preview/${item.id}`"
                class="ma-1 flex-grow-1"
                outlined
                target="_blank"
              >
                QR Code
              </app-button>
              <app-button
                v-if="!displayViewButton(item)"
                :to="`/engagements/preview/${item.id}`"
                class="ma-1 flex-grow-1"
                outlined
                target="_blank"
              >
                Preview
              </app-button>
              <app-button
                v-if="displayViewButton(item) && displayQRCodeButton(item)"
                :disabled="collateralDialog"
                :on-click="() => openCollateralDialog(item)"
                class="ma-1 flex-grow-1"
                target="_blank"
              >
                Assets
              </app-button>
            </template>
          </div>
        </template>
        <template v-slot:no-data>
          <v-btn color="primary" @click="RESET_FILTERS">
            No results found, reset filters.
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="collateralDialog"  width="500" content-class="app-modal-dialog">
      <v-card class="pa-8">
        <v-card-title class="justify-center grey lighten-2">
          Event Collateral
        </v-card-title>
        <v-card-text class="row pt-8">
          <div
            class="col-md-6 col-lg-6 col-6"
            v-for="opt in printableButtonList"
            :key="opt.id"
          >
            <button
              type="button"
              class="flex-grow-2 events-search-form__submit v-btn v-btn--block v-btn--has-bg v-btn--tile theme--light v-size--default app-button text-heading"
              style="height: 50px;"
              @click="downloadItem(opt.value)"
              :class="{ 'btn__primary magenta v-btn--is-elevated white--text': !downloading, 'v-btn--loading': downloading }"
              :disabled="downloading"
            >
              <v-progress-circular v-if="downloading" indeterminate />
              <span v-if="!downloading" class="v-btn__content">{{ opt.text }}</span>
            </button>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="reportingDialog" width="500" content-class="app-modal-dialog">
      <v-card class="pa-8">
        <v-card-title class="justify-center grey lighten-2">
          Event Reporting Download
        </v-card-title>
        <v-card-text class="row pt-8">
          <app-button
            :on-click="() => downloadReportPDF()"
            :disabled="downloading"
            class="ma-1 flex-grow-1"
          >
            Download PDF
          </app-button>

          <app-button
            :on-click="() => downloadReportCSV()"
            :disabled="downloading"
            class="ma-1 flex-grow-1"
          >
            Download CSV
          </app-button>
        </v-card-text>
      </v-card>
    </v-dialog>

    <PDFGenerator
      ref="generator"
      :cur-event="eventObject"
      @flyerGenerationComplete="closeCollateralDialog()"
      @aFrameGenerationComplete="closeCollateralDialog()"
    />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import AppButton from './AppButton.vue';
import PDFGenerator from '@/components/PDFGenerator.vue';
import ExportTools from '@/utility/ExportTools.js';

export default {
  name: 'AllEventsTable',
  props: {
    tableData: {
      type: Array,
      default: () => {
        return [];
      }
    },
    isLoading: {
      type: Boolean,
      default: true
    },
    rowsPerPage: {
      type: Number,
      default: 25
    }
  },
  components: {
    PDFGenerator,
    AppButton
  },
  data() {
    return {
      collateralDialog: false,
      downloading: false,
      eventObject: null,
      headers: [
        {
          text: 'ID',
          align: 'cell',

          value: 'id',
          width: '5%',
          class: 'abc'
        },
        {
          text: 'Start',
          align: 'cell',

          value: 'eventStartDate',
          width: '5%',
          class: 'abc'
        },
        { text: 'End', value: 'eventEndDate', align: 'cell', width: '5%' },
        { text: 'Event Name', value: 'name', align: 'cell', width: '10%' },
        { text: 'Event Status', value: 'event_status', align: 'cell', width: '5%' },
        { text: 'Lang', value: 'eventLanguage', align: 'cell', width: '5%' },
        {
          text: 'Created By',
          align: 'cell',

          value: 'createdByEmail',
          width: '10%',
          class: 'abc'
        },
        // {
        //   text: 'TMUS Org',
        //   value: 'organization',
        //   align: 'cell',
        //   width: '10%'
        // },
        // { text: 'Brand', value: 'brand', align: 'cell', width: '10%' },
        // { text: 'Region', value: 'region', align: 'cell', width: '5%' },
        { text: 'State', value: 'state', align: 'cell', width: '5%' },
        // { text: 'DMA', value: 'dma', align: 'cell', width: '10%' },
        {
          text: 'Scans',
          value: 'qr_code_scans',
          align: 'cell',
          width: '5%'
        },
        {
          text: 'Leads',
          value: 'engagements',
          align: 'cell',
          width: '5%'
        },
        {
          text: 'Actions',
          value: 'actions',
          align: 'center',
          sortable: false,
          width: '10%'
        }
      ],
      reportingDialog: false,
      sortBy: null,
      sortDesc: false,
      currentSort: null,
      currentSortDirection: 'down'
    };
  },
  methods: {
    sortByt(event, fieldName) {
      if (this.currentSort == fieldName) {
        this.currentSortDirection =
          this.currentSortDirection == 'down' ? 'up' : 'down';
      } else {
        this.currentSort = fieldName;
        this.currentSortDirection = 'up';
      }
      this.sortDesc = !this.sortDesc;
    },
    toggleOrder() {
      this.sortDesc = !this.sortDesc;
    },
    nextSort() {
      let index = this.headers.findIndex(h => h.value === this.sortBy);
      index = (index + 1) % this.headers.length;
      this.sortBy = this.headers[index].value;
    },
    displayViewButton: event => {
      // if today's date is before the event start date or after the event end date, show the preview button
      if (
        !event.published ||
        event.eventStartDate == null ||
        event.eventStartDate == '' ||
        event.eventEndDate == null ||
        event.eventEndDate == ''
      ) {
        return false;
      } else {
        return true;
      }
    },
    displayQRCodeButton: event => {
      if (event.experience && event.experience === 'Gametime') {
        return false;
      } else {
        return true;
      }
    },
    closeCollateralDialog() {
      this.downloading = false;
      this.collateralDialog = false;
    },
    async downloadItem(itemType) {
      this.downloading = true;
      switch (itemType) {
        case 'englishFlyer':
          await this.$refs.generator.createFlyerPDF('en');
          break;
        case 'englishAFrame':
          await this.$refs.generator.createAFramePDF('en');
          break;
        case 'spanishFlyer':
          await this.$refs.generator.createFlyerPDF('es');
          break;
        case 'spanishAFrame':
          await this.$refs.generator.createAFramePDF('es');
          break;
        default:
          this.downloading = false;
          console.log('No action for', itemType);
      }
    },
    openCollateralDialog(item) {
      this.eventObject = item;
      this.collateralDialog = true;
    },
    openReportingDialog(item) {
      this.eventObject = item;
      this.reportingDialog = true;
    },
    async downloadReportPDF() {
      this.downloading = true;
      try {
        const details = await this.$store.dispatch('EVENT_MANAGER_STORE/LOAD_EVENT', this.eventObject.id);
        if (details && details.id && this.$refs.generator) {
          await this.$refs.generator.createReportingPDF(details);
        }
      } catch (e) {
        console.error("Error downloading report PDF:", e);
      } finally {
        this.downloading = false;
      }
    },
    async downloadReportCSV() {
      this.downloading = true;
      try {
        const details = await this.$store.dispatch('EVENT_MANAGER_STORE/LOAD_EVENT', this.eventObject.id);
        if (details && details.id) {
          ExportTools.exportReportingToCsv(details);
        }
      } catch (e) {
        console.error("Error downloading report CSV:", e);
      } finally {
        this.downloading = false;
        this.reportingDialog = false;
      }
},
    ...mapMutations('EVENT_TABLE_STORE', ['SET_ALL_EVENTS_SORT']),
    ...mapActions('EVENT_TABLE_STORE', ['RESET_FILTERS'])
  },
  computed: {
    ...mapState('EVENT_TABLE_STORE', [
      'RESULTS_PER_PAGE',
      'CURRENT_PAGE',
      'NUMBER_OF_PAGES',
      'LOADING',
      'ALL_EVENTS_SORT'
    ]),
    C_PAGE: {
      get() {
        return this.CURRENT_PAGE;
      },
      set(value) {
        this.$store.commit('EVENT_TABLE_STORE/SET_CURRENT_PAGE', value);
      }
    },
    isReportingPage: function() {
      return this.$route.path.includes('/event/reporting');
    },
    itemsPerPage: {
      get() {
        return this.RESULTS_PER_PAGE;
      },
      set(value) {
        this.$store.commit('EVENT_TABLE_STORE/SET_ITEMS_PER_PAGE', value);
      }
    },
    sortBySync: {
      get() {
        return this.ALL_EVENTS_SORT?.column || null;
      },
      set(value) {
        this.SET_ALL_EVENTS_SORT({
          ...this.ALL_EVENTS_SORT,
          column: value
        });
      }
    },
    sortDescSync: {
      get() {
        return this.ALL_EVENTS_SORT?.desc;
      },
      set(value) {
        this.SET_ALL_EVENTS_SORT({
          ...this.ALL_EVENTS_SORT,
          desc: value
        });
      }
    },
    printableButtonList() {
      if (!this.eventObject) return [];
      return this.eventObject.eventLanguage === 'bi'
        ? [
          { text: 'Download English Flyer', value: 'englishFlyer' },
          { text: 'Download English A-Frame', value: 'englishAFrame' },
          { text: 'Download Spanish Flyer', value: 'spanishFlyer' },
          { text: 'Download Spanish A-Frame', value: 'spanishAFrame' }
        ]
        : [
          { text: 'Download Flyer', value: 'englishFlyer' },
          { text: 'Download A-Frame', value: 'englishAFrame' }
        ];
    },
  }
};
</script>
